import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { saveIntendedUrl } from '../../utils/persistentNavigation';
import { trackEvent } from '../../utils/analytics';
import {
  createComment, deleteComment, getComments, reportComment,
  toggleCommentLike, toggleEpisodeLike,
} from '../../services/podcastApi';

/**
 * PodcastComments — the logged-in social layer on an episode page (CEL-1355,
 * PRD §2.4). Reads render for everyone; writes 401 → the login CTA rides the
 * persistentNavigation return rail (R2: a conversion surface, not an error
 * state — post-auth the user lands back on this episode).
 *
 * Shape: flat comments + single-level replies (R11). Author sees delete on own
 * comments; admins see delete everywhere + an inline reported-badge (R12/R14
 * post-moderation v1 — a dedicated queue page is deferred until volume).
 */
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch (e) {
    return '';
  }
};

const PodcastComments = ({ slug, seedPrompt, apiRef, onSocialChange }) => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const isAdmin = Boolean(userProfile && userProfile.is_admin);

  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [body, setBody] = useState('');
  const [replyTo, setReplyTo] = useState(null); // comment id being replied to
  const [replyBody, setReplyBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState('');

  const load = useCallback(() => {
    getComments(slug)
      .then((res) => { setData(res.data); setLoadError(false); })
      .catch(() => setLoadError(true));
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const goLogin = (location) => {
    trackEvent('podcast_login_cta_click', { episode: slug, location });
    saveIntendedUrl(`/podcast/${slug}/`);
    navigate('/login');
  };

  const authed = Boolean(data && data.authenticated);

  const withAuthGuard = (fn, location) => (...args) => {
    if (!authed) { goLogin(location); return; }
    fn(...args);
  };

  const submitComment = (parentId, text, done) => {
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    setNotice('');
    createComment(slug, text.trim(), parentId)
      .then(() => {
        trackEvent('podcast_comment_created', { episode: slug, is_reply: Boolean(parentId) });
        done();
        load();
      })
      .catch((err) => {
        if (err.response && err.response.status === 401) goLogin('comment-box');
        else if (err.response && err.response.status === 429) setNotice("You're commenting fast — try again in a bit.");
        else setNotice("Couldn't post the comment. Try again.");
      })
      .finally(() => setSubmitting(false));
  };

  const onEpisodeLike = withAuthGuard(() => {
    toggleEpisodeLike(slug).then((res) => {
      trackEvent('podcast_like', { episode: slug, target: 'episode', liked: res.data.liked });
      setData((d) => ({
        ...d, episode_likes: res.data.like_count, episode_liked_by_me: res.data.liked,
      }));
    }).catch(() => {});
  }, 'episode-like');

  // Surface the episode's social state to the page (the header like + comment
  // count — Nitish, shift 102: liking shouldn't require scrolling to the
  // bottom). One component owns the state; the header renders a mirror.
  useEffect(() => {
    if (data && onSocialChange) {
      onSocialChange({
        episode_likes: data.episode_likes,
        episode_liked_by_me: data.episode_liked_by_me,
        comment_count: data.comment_count,
        authenticated: data.authenticated,
      });
    }
    // onSocialChange is a stable setState from the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Same-state like toggle for the page header (PodcastPlayer's apiRef pattern).
  useEffect(() => {
    if (apiRef) apiRef.current = { toggleEpisodeLike: onEpisodeLike };
  });

  const onCommentLike = withAuthGuard((comment) => {
    toggleCommentLike(comment.id).then((res) => {
      trackEvent('podcast_like', { episode: slug, target: 'comment', liked: res.data.liked });
      load();
    }).catch(() => {});
  }, 'comment-like');

  const onDelete = (comment) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this comment?')) return;
    deleteComment(comment.id).then(load).catch(() => {});
  };

  const onReport = withAuthGuard((comment) => {
    reportComment(comment.id).then(() => setNotice('Reported — thanks for flagging.')).catch(() => {});
  }, 'report');

  const renderComment = (c, isReply) => (
    <div className={`pod-cmt ${isReply ? 'pod-cmt--reply' : ''}`} key={c.id}>
      <div className="pod-cmt__head">
        {c.author.profile_picture ? (
          <img className="pod-cmt__avatar" src={c.author.profile_picture} alt="" width="28" height="28" />
        ) : (
          <span className="pod-cmt__avatar pod-cmt__avatar--blank" aria-hidden="true">
            {(c.author.name || '?').slice(0, 1).toUpperCase()}
          </span>
        )}
        <span className="pod-cmt__name">{c.author.name}</span>
        <span className="pod-cmt__date">{fmtDate(c.created_at)}</span>
        {isAdmin && c.report_count > 0 && (
          <span className="pod-cmt__reported">⚑ {c.report_count} report{c.report_count === 1 ? '' : 's'}</span>
        )}
      </div>
      {c.is_deleted ? (
        <p className="pod-cmt__body pod-cmt__body--deleted">Comment deleted</p>
      ) : (
        <p className="pod-cmt__body">{c.body}</p>
      )}
      {!c.is_deleted && (
        <div className="pod-cmt__actions">
          <button
            type="button"
            className={`pod-cmt__act ${c.liked_by_me ? 'is-on' : ''}`}
            onClick={() => onCommentLike(c)}
          >
            ♥ {c.like_count > 0 ? c.like_count : ''}
          </button>
          {!isReply && (
            <button
              type="button"
              className="pod-cmt__act"
              onClick={() => (authed ? setReplyTo(replyTo === c.id ? null : c.id) : goLogin('reply'))}
            >
              Reply
            </button>
          )}
          {(c.is_mine || isAdmin) && (
            <button type="button" className="pod-cmt__act" onClick={() => onDelete(c)}>
              Delete
            </button>
          )}
          {!c.is_mine && (
            <button type="button" className="pod-cmt__act" onClick={() => onReport(c)}>
              Report
            </button>
          )}
        </div>
      )}
      {replyTo === c.id && (
        <div className="pod-cmt__replybox">
          <textarea
            className="pod-cmts__input"
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            maxLength={2000}
            rows={2}
            placeholder={`Reply to ${c.author.name}…`}
          />
          <button
            type="button"
            className="pod-cmts__submit"
            disabled={submitting || !replyBody.trim()}
            onClick={() => submitComment(c.id, replyBody, () => { setReplyBody(''); setReplyTo(null); })}
          >
            Reply
          </button>
        </div>
      )}
      {c.replies && c.replies.length > 0 && (
        <div className="pod-cmt__replies">
          {c.replies.map((r) => renderComment(r, true))}
        </div>
      )}
    </div>
  );

  return (
    <section className="pod-cmts" id="comments" aria-label="Comments">
      <div className="pod-cmts__head">
        <h2 className="pod-cmts__title">
          Comments{data ? ` (${data.comment_count})` : ''}
        </h2>
        {data && (
          <button
            type="button"
            className={`pod-cmts__eplike ${data.episode_liked_by_me ? 'is-on' : ''}`}
            onClick={onEpisodeLike}
            aria-pressed={data.episode_liked_by_me}
          >
            ♥ {data.episode_likes} like{data.episode_likes === 1 ? '' : 's'}
          </button>
        )}
      </div>

      {loadError && (
        <p className="pod-cmts__note">Comments are unavailable right now — the episode plays fine without them.</p>
      )}

      {data && !authed && (
        <div className="pod-cmts__cta">
          <p>Log in to join the conversation — free account, no credit card.</p>
          <button type="button" className="pod-cmts__ctabtn" onClick={() => goLogin('comment-box')}>
            Log in to comment
          </button>
        </div>
      )}

      {data && authed && (
        <div className="pod-cmts__box">
          <textarea
            className="pod-cmts__input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={2000}
            rows={3}
            placeholder={seedPrompt || 'Share your thoughts…'}
          />
          <button
            type="button"
            className="pod-cmts__submit"
            disabled={submitting || !body.trim()}
            onClick={() => submitComment(null, body, () => setBody(''))}
          >
            Post comment
          </button>
        </div>
      )}

      {notice && <p className="pod-cmts__note">{notice}</p>}

      {data && data.comments.length === 0 && (
        <p className="pod-cmts__empty">{seedPrompt || 'Be the first to comment.'}</p>
      )}

      {data && data.comments.map((c) => renderComment(c, false))}
    </section>
  );
};

export default PodcastComments;
